import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { OktaService } from './okta.service';
import { environment } from '../../../environments/environment';
import { IconElement, ImageElement } from './catalog.service';
import { ServiceElement } from './service.service';
import { TechnologyElement } from './technology.service';

export interface SuiteElement {
    'suiteID': number,
    'name': string,
    'item': number,
    'typeID': number,
    'productLineID': number,
    'statusID': number,
    'slaid': number,
    'published': boolean,
    'revisionDate': string,
    'tagline': string,
    'problem': string,
    'approach': string,
    'features': string,
    'images': ImageElement[],
    'icons': IconElement,
    'techDescription': string,
    'docsURL': string,
    'communityURL': string,
    'associatedTechnology': TechnologyElement[],
    'associatedServices': ServiceElement[],
    'previousNames': string[],
    'shortDescription': string,
    'subtitle': string,
    'solutionArea': number,
    'solutionCapability': string
}

@Injectable()
export class SuiteService {
    suiteList: BehaviorSubject<SuiteElement[] | null> = new BehaviorSubject<SuiteElement[] | null>(null);
    loadFailed = false;

    constructor(private http: HttpClient, private oktaService: OktaService) { };

    get suiteUpdated(): Observable<SuiteElement[] | null> {
        return this.suiteList.asObservable();
    }

    refresh() {
        this.loadFailed = false;

        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        this.http.get<any>(environment.azure.url + '/admin.suites', options)
            .subscribe({
                next: data => {
                    this.suiteList.next(data);
                    this.loadFailed = false;
                },
                error: msg => {
                    this.loadFailed = true;
                    this.suiteList.next(null);
                    console.log(msg);
                }
            });
    }

    detail(suiteID: number): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        return this.http.get<any>(environment.azure.url + '/admin.suites/' + suiteID, options);
    }


    add(target: SuiteElement): Observable<any> {
        // Write target to database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };

        //turn associated tech and services into ids
        let assocTechIDs = [];
        const assocTechVal = target.associatedTechnology;

        assocTechVal.forEach(element => {
            assocTechIDs.push(element.technologyID);
        });

        let assocServiceIDs = [];
        const assocServiceVal = target.associatedServices;

        assocServiceVal.forEach(element => {
            assocServiceIDs.push(element.serviceID);
        });

        return this.http.post<any>(environment.azure.url + '/admin.suites.add', {
            name: target.name,
            item: target.item,
            typeID: target.typeID,
            productLineID: target.productLineID,
            statusID: target.statusID,
            slaid: target.slaid,
            published: target.published,
            revisionDate: target.revisionDate,
            tagline: target.tagline,
            problem: target.problem,
            approach: target.approach,
            features: target.features,
            images: JSON.stringify(target.images),
            icons: JSON.stringify(target.icons),
            techDescription: target.techDescription,
            docsURL: target.docsURL,
            communityURL: target.communityURL,
            associatedTechnology: target.associatedTechnology,
            associatedServices: target.associatedServices,
            previousNames: JSON.stringify(target.previousNames),
            shortDescription: target.shortDescription,
            subtitle: target.subtitle,
            solutionArea: target.solutionArea,
            solutionCapability: target.solutionCapability
        }, options)
    }

    update(target: SuiteElement): Observable<any> {
        // Update target in database
        let headers = new HttpHeaders();
        headers = headers.append('Content-Type', 'application/json');
        headers = headers.append('Authorization', `Bearer ${this.oktaService.accessToken}`);
        const options = { headers: headers };
        return this.http.post<any>(environment.azure.url + '/admin.suites.update', {
            suiteID: target.suiteID,
            name: target.name,
            item: target.item,
            typeID: target.typeID,
            productLineID: target.productLineID,
            statusID: target.statusID,
            slaid: target.slaid,
            published: target.published,
            revisionDate: target.revisionDate,
            tagline: target.tagline,
            problem: target.problem,
            approach: target.approach,
            features: target.features,
            images: JSON.stringify(target.images),
            icons: JSON.stringify(target.icons),
            techDescription: target.techDescription,
            docsURL: target.docsURL,
            communityURL: target.communityURL,
            associatedTechnology: target.associatedTechnology,
            associatedServices: target.associatedServices,
            previousNames: JSON.stringify(target.previousNames),
            shortDescription: target.shortDescription,
            subtitle: target.subtitle,
            solutionArea: target.solutionArea,
            solutionCapability: target.solutionCapability
        }, options)
    }
}